/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Compliance Tab - Uses real game data for compliance metrics
 */

import React, { useMemo } from 'react';
import { CityStats, Grid, BuildingType, SimulationBreakdown } from '../../../types';
import { BUILDINGS } from '../../../constants';

interface ComplianceTabProps {
  stats: CityStats;
  grid: Grid;
  simBreakdown?: SimulationBreakdown;
}

const ComplianceTab: React.FC<ComplianceTabProps> = ({ stats, grid, simBreakdown }) => {
  // Calculate real metrics from grid
  const metrics = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalBuildings = 0;
    let connectedBuildings = 0;
    let complianceBuildings = 0;
    let securityBuildings = 0;
    let legalBuildings = 0;

    grid.flat().forEach(tile => {
      if (tile.buildingType !== BuildingType.None) {
        totalBuildings++;
        counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
        
        if (tile.isConnected) {
          connectedBuildings++;
          const config = BUILDINGS[tile.buildingType];
          if (config.category === 'Governance' || config.category === 'EnhancedCompliance') {
            complianceBuildings++;
          }
          if (config.category === 'Security' || config.category === 'EnhancedSecurity' || config.category === 'Cyber') {
            securityBuildings++;
          }
          if (config.category === 'Legal') {
            legalBuildings++;
          }
        }
      }
    });

    // Calculate dynamic metrics based on actual game state
    const policyHQCount = counts[BuildingType.ComplianceMgmt] || 0;
    const auditLabCount = counts[BuildingType.Monitoring] || 0;
    const gdprOfficeCount = counts[BuildingType.GDPROffice] || 0;
    const dpoCount = counts[BuildingType.DataProtectionOfficer] || 0;
    const internalAuditCount = counts[BuildingType.InternalAudit] || 0;
    const contMonitoringCount = counts[BuildingType.ContinuousMonitoring] || 0;
    const regulatoryCount = counts[BuildingType.RegulatoryRelations] || 0;
    const privacyCount = counts[BuildingType.LegalPrivacy] || 0;

    // Policy Coverage based on compliance buildings
    const policyCoverage = Math.min(100, 40 + (policyHQCount * 15) + (gdprOfficeCount * 10) + (dpoCount * 10) + (regulatoryCount * 10));
    
    // Audit Readiness based on audit buildings
    const auditReadiness = Math.min(100, 30 + (auditLabCount * 20) + (internalAuditCount * 15) + (contMonitoringCount * 15));
    
    // Training based on security awareness
    const securityTrainingCount = counts[BuildingType.SecurityTraining] || 0;
    const trainingCompletion = Math.min(100, 50 + (securityTrainingCount * 25) + (complianceBuildings * 5));
    
    // Documentation based on legal buildings
    const documentation = Math.min(100, 40 + (privacyCount * 15) + (legalBuildings * 10) + (dpoCount * 10));

    return {
      counts,
      totalBuildings,
      connectedBuildings,
      complianceBuildings,
      securityBuildings,
      legalBuildings,
      policyCoverage,
      auditReadiness,
      trainingCompletion,
      documentation
    };
  }, [grid]);

  const getStatusFromValue = (value: number) => {
    if (value >= 90) return { status: 'excellent', color: 'text-emerald-400' };
    if (value >= 70) return { status: 'good', color: 'text-cyan-400' };
    if (value >= 50) return { status: 'moderate', color: 'text-yellow-400' };
    return { status: 'needs work', color: 'text-red-400' };
  };

  const complianceMetrics = [
    { name: 'Policy Coverage', value: metrics.policyCoverage, ...getStatusFromValue(metrics.policyCoverage) },
    { name: 'Audit Readiness', value: metrics.auditReadiness, ...getStatusFromValue(metrics.auditReadiness) },
    { name: 'Training Completion', value: metrics.trainingCompletion, ...getStatusFromValue(metrics.trainingCompletion) },
    { name: 'Documentation', value: metrics.documentation, ...getStatusFromValue(metrics.documentation) }
  ];

  // Generate dynamic activities based on game state
  const activities = useMemo(() => {
    const acts = [];
    
    if (simBreakdown && simBreakdown.compliance.generated > 0) {
      acts.push({
        text: `Generating +${simBreakdown.compliance.generated} compliance per tick`,
        time: 'Active',
        color: 'cyan',
        pulse: true
      });
    }
    
    if (metrics.complianceBuildings > 0) {
      acts.push({
        text: `${metrics.complianceBuildings} compliance buildings operational`,
        time: 'Current',
        color: 'emerald',
        pulse: false
      });
    }
    
    if (stats.compliance >= 80) {
      acts.push({
        text: 'Compliance score in excellent range',
        time: 'Status',
        color: 'emerald',
        pulse: false
      });
    } else if (stats.compliance < 50) {
      acts.push({
        text: 'Warning: Compliance below threshold',
        time: 'Alert',
        color: 'red',
        pulse: true
      });
    }
    
    if (metrics.legalBuildings > 0) {
      acts.push({
        text: `${metrics.legalBuildings} legal departments active`,
        time: 'Current',
        color: 'purple',
        pulse: false
      });
    }

    if (simBreakdown && simBreakdown.compliance.productRisk > 0) {
      acts.push({
        text: `Product risk draining -${simBreakdown.compliance.productRisk.toFixed(1)} compliance`,
        time: 'Warning',
        color: 'yellow',
        pulse: true
      });
    }

    return acts.length > 0 ? acts : [
      { text: 'Build compliance infrastructure to see activity', time: 'Tip', color: 'slate', pulse: false }
    ];
  }, [simBreakdown, metrics, stats.compliance]);

  const getColorClasses = (color: string) => {
    const colors: Record<string, { border: string; bg: string; dot: string }> = {
      cyan: { border: 'border-cyan-500', bg: 'bg-cyan-400', dot: 'bg-cyan-400' },
      emerald: { border: 'border-emerald-500', bg: 'bg-emerald-400', dot: 'bg-emerald-400' },
      yellow: { border: 'border-yellow-500', bg: 'bg-yellow-400', dot: 'bg-yellow-400' },
      red: { border: 'border-red-500', bg: 'bg-red-400', dot: 'bg-red-400' },
      purple: { border: 'border-purple-500', bg: 'bg-purple-400', dot: 'bg-purple-400' },
      slate: { border: 'border-slate-500', bg: 'bg-slate-400', dot: 'bg-slate-400' }
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Compliance Management</h3>
        <p className="text-slate-400 mt-2">Monitor and manage regulatory compliance across all operations</p>
      </div>

      {/* Compliance Score */}
      <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <div className="text-5xl font-black text-cyan-400 mb-2">
              {stats.compliance}%
            </div>
            <div className="text-sm text-slate-300 uppercase tracking-wider">Overall Compliance Score</div>
          </div>
          {simBreakdown && (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-emerald-400">+{simBreakdown.compliance.generated}</div>
                <div className="text-xs text-slate-400">Generated</div>
              </div>
              <div>
                <div className="text-lg font-bold text-red-400">-{simBreakdown.compliance.decay}</div>
                <div className="text-xs text-slate-400">Decay</div>
              </div>
              <div>
                <div className={`text-lg font-bold ${simBreakdown.compliance.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {simBreakdown.compliance.net >= 0 ? '+' : ''}{simBreakdown.compliance.net.toFixed(1)}
                </div>
                <div className="text-xs text-slate-400">Net/Tick</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {complianceMetrics.map((metric, idx) => (
          <div key={idx} className="bg-slate-900/30 border border-white/10 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white font-medium">{metric.name}</span>
              <span className={`text-sm font-bold ${metric.color}`}>
                {metric.value}% - {metric.status}
              </span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  metric.value >= 90 ? 'bg-gradient-to-r from-emerald-600 to-emerald-400' :
                  metric.value >= 70 ? 'bg-gradient-to-r from-cyan-600 to-blue-500' :
                  metric.value >= 50 ? 'bg-gradient-to-r from-yellow-600 to-yellow-400' :
                  'bg-gradient-to-r from-red-600 to-red-400'
                }`}
                style={{ width: `${metric.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Building Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-cyan-400">{metrics.complianceBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Compliance Buildings</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{metrics.securityBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Security Buildings</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-400">{metrics.legalBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Legal Buildings</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-white">{metrics.connectedBuildings}/{metrics.totalBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Connected</div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
        <h4 className="text-lg font-bold text-white mb-4">Compliance Activity Feed</h4>
        <div className="space-y-3">
          {activities.map((activity, idx) => {
            const colors = getColorClasses(activity.color);
            return (
              <div key={idx} className={`flex items-center gap-3 p-3 bg-white/5 rounded border-l-4 ${colors.border}`}>
                <div className={`w-2 h-2 ${colors.dot} rounded-full ${activity.pulse ? 'animate-pulse' : ''}`}></div>
                <div className="flex-1">
                  <div className="text-white text-sm">{activity.text}</div>
                  <div className="text-xs text-slate-400">{activity.time}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ComplianceTab;
